export type VendiaRuntimeUser = {
  id?: string;
  name?: string;
  username?: string;
  email?: string;
  roles?: Array<{ id?: string; name: string }>;
  all_permissions?: string[];
};

export type VendiaRuntime = {
  appName: string;
  appSubtitle: string;
  tenantName: string | null;
  basePath: string;
  baseUrl: string;
  adminAppUrl: string;
  csrfToken: string | null;
  user: VendiaRuntimeUser | null;
  locationName: string | null;
};

type LegacyLocation = { label?: string; name?: string } | string | null | undefined;

declare global {
  interface Window {
    __VENDIA_REACT__?: Partial<VendiaRuntime>;
    data?: {
      user?: VendiaRuntimeUser | null;
      location?: { label?: string; name?: string } | string | null;
      settings?: {
        baseURL?: string;
        data?: { name?: string };
      };
      tenant?: { name?: string } | string | null;
    };
  }
}

function csrfTokenFromMeta() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? null;
}

function normalizeLocationName(location: LegacyLocation) {
  if (!location) return null;
  if (typeof location === 'string') return location;
  return location.label ?? location.name ?? null;
}

function normalizeTenantName(value: unknown) {
  if (typeof value !== 'string' || !value.trim()) return null;
  return value.trim();
}

function tenantNameFromUrl(value: string) {
  try {
    const hostname = new URL(value, window.location.origin).hostname.toLowerCase();
    const labels = hostname.split('.').filter(Boolean);
    if (labels.length < 3 || labels[0] === 'www') return null;
    return labels[0];
  } catch {
    return null;
  }
}

function tenantNameFromBrowserHost() {
  const hostname = window.location.hostname.toLowerCase();
  const labels = hostname.split('.').filter(Boolean);
  if (labels.length < 3 || labels[0] === 'www') return null;
  return labels[0];
}

export function getRuntimeConfig(): VendiaRuntime {
  const legacyData = window.data ?? {};
  const configured = window.__VENDIA_REACT__ ?? {};
  const envBackendUrl = import.meta.env.VITE_VENDIA_BACKEND_URL ?? import.meta.env.VITE_APP_API_URL;
  const envAdminAppUrl = import.meta.env.VITE_VENDIA_ADMIN_APP_URL;
  const baseUrl = configured.baseUrl ?? legacyData.settings?.baseURL ?? envBackendUrl ?? window.location.origin;
  const basePath = configured.basePath ?? import.meta.env.VITE_APP_BASE_NAME ?? '/';
  const configuredTenant = normalizeTenantName(configured.tenantName);
  const injectedTenant = typeof legacyData.tenant === 'string'
    ? legacyData.tenant
    : legacyData.tenant?.name;

  return {
    appName: configured.appName ?? 'Vendia Business',
    appSubtitle: configured.appSubtitle ?? 'Business Platform',
    tenantName: configuredTenant ?? normalizeTenantName(injectedTenant) ?? tenantNameFromBrowserHost() ?? tenantNameFromUrl(baseUrl),
    basePath,
    baseUrl,
    adminAppUrl: configured.adminAppUrl ?? envAdminAppUrl ?? `${baseUrl.replace(/\/$/, '')}/admin/app`,
    csrfToken: configured.csrfToken ?? csrfTokenFromMeta(),
    user: configured.user ?? legacyData.user ?? null,
    locationName: configured.locationName ?? normalizeLocationName(legacyData.location)
  };
}
